import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ProjectDeleteModalService } from './project-delete-modal.service';
import * as _ from 'lodash-es';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { buildHeaderDataShell, generalConfigFromGlobal, normalizeFilterParams, rowPayloadFromActiveScreen } from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-project-delete-modal',
    templateUrl: './project-delete-modal.component.html',
    styleUrls: ['./project-delete-modal.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ProjectDeleteModalComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  private readonly subs = new Subscription();

  filterParams: any = {};
  baseURLs: any = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  rowData: any = {};

  // 🚀 NEW: Screen type input to distinguish between Project and Project Step deletion
  @Input('screen-type')
  set _screen_type(screenType: string){
    try{
      if(screenType && screenType.length > 0){
        this.screenType = screenType;
        this.init();
      }
    }catch(e){
      console.log("Screen type input not yet stable. Still waiting to get resolved.");
    }
  }
  screenType: string = 'PROJECT'; // Default to PROJECT deletion

  // Modal configuration - following AngularJS pattern
  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm Delete',
    message: 'Are you sure you want to delete?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  // Component properties following fir163jsync pattern
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  
  showModal: boolean = false; // Show modal by default for delete confirmation
  deleteSaveDtls: any[] = [];
  logged_in_user: string = '';
  modalName: string = '';

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private gtwSnackbarService: GtwSnackbarService,
    private projectDeleteModalService: ProjectDeleteModalService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
  }

  init(): void {
    // Following fir163jsync pattern - check if all required data is available
    if (this.baseURLs && Object.keys(this.baseURLs).length > 0 && 
        this.filterParams && Object.keys(this.filterParams).length > 0 &&
        this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0 &&
        this.screenType && this.screenType.length > 0) {
      this.initializeData();
    }
  }

  initializeData(): void {
    // Get logged in user from USER_SETTINGS like fir163jsync
    if (this.USER_SETTINGS?.user?.sso_id) {
      this.logged_in_user = this.USER_SETTINGS.user.sso_id;
    }
    
    // Get modal name from globalService or filterParams
    if (this.globalService?.modalScreen?.modal_name) {
      this.modalName = this.globalService.modalScreen.modal_name;
    }
    
    // Process grid data if rowData is available
    if (this.rowData) {
      this.processGridData();
    }
  }

  processGridData(): void {
    // Process grid data exactly like AngularJS controller
    if (this.rowData != null) {
      this.deleteSaveDtls = [];
      
      // Clone the grid data like AngularJS
      const tempGridData = _.clone(this.rowData);
      const gridDataMultipleSelect = _.clone(tempGridData);
      
      if (gridDataMultipleSelect != undefined) {
        for (var key in gridDataMultipleSelect) {
          const gridObj: any = {};
          
          // Access data property like AngularJS: vm.gridDataMultipleSelect[key].data
          const dataItem = gridDataMultipleSelect[key].data || gridDataMultipleSelect[key];
          
          // Extract relevant fields based on screen type - following AngularJS delete function
          if (this.screenType === 'PROJECT') {
            // Project deletion fields
            gridObj["PROJECT_ID"] = dataItem.PROJECT_ID;
            gridObj["PROJECT_NAME"] = dataItem.PROJECT_NAME;
            gridObj["PROJECT_DESC"] = dataItem.PROJECT_DESC;
            // Add other Project fields as needed
          } else if (this.screenType === 'PROJECT_STEP') {
            // Project Step deletion fields
            gridObj["PROJECT_STEP_ID"] = dataItem.PROJECT_STEP_ID;
            gridObj["PROJECT_ID"] = dataItem.PROJECT_ID;
            gridObj["STEP_NAME"] = dataItem.STEP_NAME;
            gridObj["STEP_DESC"] = dataItem.STEP_DESC;
            // Add other Project Step fields as needed
          }

          this.deleteSaveDtls.push(gridObj);
        }
      }
    }
  }

  onConfirm(): void {
    this.confirmDelete();
  }

  cancel(): void {
    // Following fir163jsync pattern - dispatch CustomEvent to close modal
    this.showModal = false;
    
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });
    
    if (document.querySelector('gtw-project-delete-modal-ce')) {
      document.querySelector('gtw-project-delete-modal-ce')?.dispatchEvent(updateEvent);
    }
  }
  
  onCancel(): void {
    // Called from confirmation modal - delegates to cancel()
    this.cancel();
  }

  confirmDelete(): void {
    // Follow exact AngularJS behavior
    const message = "Data deleted successfully!";
    
    // Call sendDetails function like AngularJS
    this.sendDetails(this.deleteSaveDtls, message);
  }

  sendDetails(deleteData: any[], message: string): void {
    // Show loader in the modal - update modalConfig to trigger change detection
    this.modalConfig = { ...this.modalConfig, loading: true };
    
    // Call appropriate service method based on screen type
    let serviceCall: any;
    if (this.screenType === 'PROJECT') {
      serviceCall = this.projectDeleteModalService.deleteProjectData(
        this.GENERAL_CONFIG.base_url, // Use base_url like fir163jsync
        deleteData, // Pass deleteData array (populated from grid data)
        this.globalService, // Pass globalService like fir163jsync
        this.filterParams, // Pass filterParams
        this.rowData // Pass rowData
      );
    } else if (this.screenType === 'PROJECT_STEP') {
      serviceCall = this.projectDeleteModalService.deleteProjectStepData(
        this.GENERAL_CONFIG.base_url, // Use base_url like fir163jsync
        deleteData, // Pass deleteData array (populated from grid data)
        this.globalService, // Pass globalService like fir163jsync
        this.filterParams, // Pass filterParams
        this.rowData // Pass rowData
      );
    }

    if (serviceCall) {
      serviceCall.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (result: any) => {
          // Hide loader - update modalConfig to trigger change detection
          this.modalConfig = { ...this.modalConfig, loading: false };
          
          // Handle response exactly like AngularJS
          if (result.callSuccess === '1') {
            // Show success message AFTER API call succeeds (like AngularJS line 54 and 133)
            this.gtwSnackbarService.openSnackBar('Data deleted successfully!', 'gtw-snackbar--green');
            
            // 🚀 CRITICAL: Emit 'refresh' string for AngularJS modal close
            this.requestGridUpdate.emit('refresh');
            this.showModal = false; // Hide modal after successful operation
            // this.cancel();
            // AngularJS handles modal close automatically
          } else {
            this.gtwSnackbarService.openSnackBar(result.errorMessage || 'Something went wrong, please try again!', 'gtw-snackbar--red');
          }
        },
        (error: any) => {
          // Hide loader on error - update modalConfig to trigger change detection
          this.modalConfig = { ...this.modalConfig, loading: false };
          console.error('Delete error:', error);
          this.gtwSnackbarService.openSnackBar('Something went wrong, please try again!', 'gtw-snackbar--red');
        }
      );
    }
  }
}
